import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityBotKucoinAccount,
  ActivityBotSectorOption,
  cryptoPortfolioService,
  GrowthChallenge,
  GrowthChallengeLadderStep,
} from '../../services/cryptoPortfolioService';

const DEFAULT_SECTORS = ['MEME', 'LAYER_1', 'COMMODITIES'];
const MIN_SECTORS = 1;
const MIN_START = 10;
const MAX_START = 50;

function fmtUsd(n: number): string {
  return `$${n.toFixed(2).replace(/\.00$/, '')}`;
}

function defaultLadder(start: number): GrowthChallengeLadderStep[] {
  return [
    { atUsd: start * 2, lockUsd: start * 0.5 },
    { atUsd: start * 4, lockUsd: start },
    { atUsd: start * 7, lockUsd: start * 2 },
  ];
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 700,
  color: '#334155',
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  fontSize: 13,
  boxSizing: 'border-box',
};

interface GrowthChallengeWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (challenge: GrowthChallenge) => void;
}

const GrowthChallengeWizard: React.FC<GrowthChallengeWizardProps> = ({ open, onClose, onCreated }) => {
  const [accounts, setAccounts] = useState<ActivityBotKucoinAccount[]>([]);
  const [sectorOptions, setSectorOptions] = useState<ActivityBotSectorOption[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [kucoinConfigId, setKucoinConfigId] = useState<number | ''>('');
  const [name, setName] = useState('');
  const [startAmount, setStartAmount] = useState('10');
  const [targetAmount, setTargetAmount] = useState('100');
  const [floorAmount, setFloorAmount] = useState('5');
  const [selectedSectors, setSelectedSectors] = useState<string[]>(DEFAULT_SECTORS);
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmation('');
    setLoadingMeta(true);
    Promise.all([
      cryptoPortfolioService.listActivityBotKucoinAccounts(),
      cryptoPortfolioService.listActivityBotSectors(),
    ])
      .then(([accs, sectors]) => {
        setAccounts(accs);
        setSectorOptions(sectors);
        const firstSelectable = accs.find(a => a.selectable !== false);
        if (firstSelectable) setKucoinConfigId(firstSelectable.id);
      })
      .catch(() => setError('Failed to load KuCoin accounts and sectors'))
      .finally(() => setLoadingMeta(false));
  }, [open]);

  const start = Number(startAmount);
  const target = Number(targetAmount);
  const floor = Number(floorAmount);
  const startValid = Number.isFinite(start) && start >= MIN_START && start <= MAX_START;
  const expectedConfirmation = startValid ? `RISK $${start % 1 === 0 ? start.toFixed(0) : start}` : 'RISK $…';
  const ladder = useMemo(() => (startValid ? defaultLadder(start) : []), [startValid, start]);

  const perBot = startValid && selectedSectors.length > 0 ? start / selectedSectors.length : 0;

  const toggleSector = (sectorId: string) => {
    setSelectedSectors(prev => (prev.includes(sectorId)
      ? prev.filter(s => s !== sectorId)
      : [...prev, sectorId]));
  };

  const canSubmit = !saving
    && !loadingMeta
    && kucoinConfigId !== ''
    && startValid
    && Number.isFinite(target) && target > start
    && Number.isFinite(floor) && floor >= 0 && floor < start
    && selectedSectors.length >= MIN_SECTORS
    && confirmation.trim().toUpperCase() === expectedConfirmation.toUpperCase();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const challenge = await cryptoPortfolioService.createGrowthChallenge({
        name: name.trim() || null,
        kucoinConfigId: Number(kucoinConfigId),
        startBalanceUsd: start,
        targetBalanceUsd: target,
        floorBalanceUsd: floor,
        sectorIds: selectedSectors,
        ladder,
        confirmationText: confirmation.trim(),
      });
      onCreated(challenge);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to create challenge');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1300,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 20,
          boxShadow: '0 20px 45px rgba(15, 23, 42, 0.25)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1e293b' }}>Start a Growth Challenge</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              One capped bot per sector, compounding a small bankroll toward your target.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: 18, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            margin: '12px 0',
            padding: '10px 12px',
            borderRadius: 10,
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            color: '#92400e',
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <strong>Honest odds:</strong> turning {startValid ? fmtUsd(start) : '$10'} into{' '}
          {Number.isFinite(target) && target > 0 ? fmtUsd(target) : '$100'} in days means roughly 26% compounded
          gains per day. Even with every filter enabled, the most likely outcomes are a small gain or losing the
          whole stake — treat this as lottery-ticket money. The challenge caps your loss at the amount you commit:
          it locks profit at milestones and kills the run at the floor.
        </div>

        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              borderRadius: 8,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={labelStyle}>KuCoin sub-account</label>
            <select
              value={kucoinConfigId}
              onChange={e => setKucoinConfigId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={loadingMeta || saving}
              style={inputStyle}
            >
              <option value="">Select account…</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id} disabled={a.selectable === false}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Challenge name (optional)</label>
            <input
              type="text"
              value={name}
              maxLength={80}
              placeholder="Growth Challenge $10 → $100"
              onChange={e => setName(e.target.value)}
              disabled={saving}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            <div>
              <label style={labelStyle}>Start (${MIN_START}–${MAX_START})</label>
              <input
                type="number"
                min={MIN_START}
                max={MAX_START}
                step={1}
                value={startAmount}
                onChange={e => setStartAmount(e.target.value)}
                disabled={saving}
                style={{ ...inputStyle, borderColor: startValid ? '#cbd5e1' : '#fca5a5' }}
              />
            </div>
            <div>
              <label style={labelStyle}>Target</label>
              <input
                type="number"
                min={start + 1 || 11}
                step={1}
                value={targetAmount}
                onChange={e => setTargetAmount(e.target.value)}
                disabled={saving}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Floor (kill-switch)</label>
              <input
                type="number"
                min={0}
                step={1}
                value={floorAmount}
                onChange={e => setFloorAmount(e.target.value)}
                disabled={saving}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={labelStyle}>
              Sectors (one bot per sector, {perBot > 0 ? `${fmtUsd(perBot)} each` : 'split evenly'} — more sectors = more diversification)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {sectorOptions.map(sector => {
                const checked = selectedSectors.includes(sector.id);
                return (
                  <label
                    key={sector.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: checked ? '1px solid #086afb' : '1px solid #e2e8f0',
                      background: checked ? '#eff6ff' : '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                      color: checked ? '#1d4ed8' : '#475569',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSector(sector.id)}
                      disabled={saving}
                      style={{ margin: 0 }}
                    />
                    {sector.label}
                  </label>
                );
              })}
            </div>
            {selectedSectors.length < MIN_SECTORS && (
              <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 6 }}>
                Select at least {MIN_SECTORS} sector.
              </div>
            )}
          </div>

          {ladder.length > 0 && (
            <div>
              <label style={labelStyle}>Profit-lock ladder (automatic)</label>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                {ladder.map((step, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      fontSize: 12,
                      color: '#334155',
                      borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                      background: idx % 2 === 0 ? '#f8fafc' : '#fff',
                    }}
                  >
                    <span>Equity reaches <strong>{fmtUsd(step.atUsd)}</strong></span>
                    <span>lock <strong>{fmtUsd(step.lockUsd)}</strong> profit</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                Locked profit is removed from the at-risk bankroll — bots are resized to trade only the remainder.
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>
              Type <span style={{ color: '#b91c1c' }}>{expectedConfirmation}</span> to confirm you accept losing this amount
            </label>
            <input
              type="text"
              value={confirmation}
              placeholder={expectedConfirmation}
              onChange={e => setConfirmation(e.target.value)}
              disabled={saving}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: '#334155',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                padding: '8px 18px',
                borderRadius: 999,
                border: 'none',
                background: canSubmit ? '#086afb' : '#cbd5e1',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? 'Creating bots…' : 'Start challenge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrowthChallengeWizard;
